import { Component, forwardRef } from '@angular/core';
import { PhoneNumberDialogComponent } from 'src/shared/form-models/phone-number/phone-number-dialog/phone-number-dialog.component';
import { PhoneNumber } from 'src/shared/api';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormArray,
	AbstractControl,
	FormBuilder,
	FormGroup,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil, filter } from 'rxjs/operators';
import { phoneNumberFormConfig } from 'src/shared/form-models/phone-number/phone-number.component';

@Component({
	selector: 'rpshared-phone-number-list',
	templateUrl: './phone-number-list.component.html',
	styleUrls: ['./phone-number-list.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => PhoneNumberListComponent),
			multi: true,
		},
	],
})
export class PhoneNumberListComponent extends SelfDestruct
	implements ControlValueAccessor {
	array = new FormArray([]);

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog
	) {
		super();
	}

	// model --> view
	writeValue(value: PhoneNumber[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(phoneNumberFormConfig) as FormGroup;
				fc.patchValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: PhoneNumber[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addPhoneNumber() {
		this.dialog
			.open<PhoneNumberDialogComponent, never, AbstractControl>(
				PhoneNumberDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				if (control.value.isPrimary) {
					this.removeAllPrimaryFlags();
				} else if (this.array.length < 1) {
					control.setValue({
						...control.value,
						...{ isPrimary: true },
					});
				}
				this.array.insert(0, control);
			});
	}

	editPhoneNumber(index: number) {
		this.dialog
			.open<PhoneNumberDialogComponent, PhoneNumber, AbstractControl>(
				PhoneNumberDialogComponent,
				{
					width: '450px',
					data: this.array.at(index).value,
				}
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				if (control.value.isPrimary) {
					this.removeAllPrimaryFlags();
				} else if (this.array.length < 1) {
					control.setValue({
						...control.value,
						...{ isPrimary: true },
					});
				}
				this.array.setControl(index, control);
			});
	}

	private removeAllPrimaryFlags(): void {
		const phoneNumbers = this.array.value as PhoneNumber[];

		phoneNumbers.forEach((p) => {
			p.isPrimary = false;
		});
	}
}
